import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search, Download, RotateCcw, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { adminApiService } from '../services/adminApiService';

interface Lead {
  id: string;
  name: string;
  email: string;
  source: string;
  created_at: string;
}

const AdminLeads: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Check authentication on component mount using unified JWT system
  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await adminApiService.validateToken();
      if (isValid) {
        setIsAuthenticated(true);
      } else {
        setShowPasswordModal(true);
      }
    };

    checkAuth();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setLeads(data || []);
      setFilteredLeads(data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch leads';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredLeads(leads);
    } else {
      const filtered = leads.filter(lead =>
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.source.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLeads(filtered);
    }
  }, [searchTerm, leads]);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    setShowPasswordModal(false);
  };

  const handleApiLogin = async (password: string): Promise<boolean> => {
    const result = await adminApiService.login(password);
    if (result.success) {
      setIsAuthenticated(true);
      setShowPasswordModal(false);
      return true;
    } else {
      setError(result.error || 'Login failed');
      return false;
    }
  };

  const handleLogout = () => {
    // Use unified logout system
    adminApiService.logout();
    setIsAuthenticated(false);
    
    // Also clear old session storage for consistency
    sessionStorage.removeItem('admin_authenticated');
    sessionStorage.removeItem('admin_auth_time');
    
    navigate('/');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'hero_cta':
      case 'RiC_Hero_CTA':
        return 'primary';
      default:
        return 'default';
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Source', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...filteredLeads.map(lead => [
        `"${lead.name}"`,
        `"${lead.email}"`,
        `"${lead.source}"`,
        `"${formatDate(lead.created_at)}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Unified password modal (replaces AdminPasswordModal)
  const CustomPasswordModal = ({ open, onAuthenticated }: { open: boolean; onAuthenticated: () => void }) => {
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoginLoading(true);
      setLoginError('');

      const success = await handleApiLogin(password);
      if (success) {
        onAuthenticated();
        setPassword('');
      } else {
        setLoginError('Invalid password');
      }
      setLoginLoading(false);
    };

    return (
      <Dialog open={open} disableEscapeKeyDown>
        <DialogTitle>Admin Authentication</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Admin Password"
              type="password"
              fullWidth
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!loginError}
              helperText={loginError}
              disabled={loginLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              navigate('/');
              // Dispatch event to close admin panel
              window.dispatchEvent(new CustomEvent('admin-cancel'));
            }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={!password || loginLoading}
            >
              {loginLoading ? <CircularProgress size={20} /> : 'Login'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  };

  // Show password modal if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <CustomPasswordModal 
          open={showPasswordModal} 
          onAuthenticated={handleAuthenticated}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Lead Management
      </Typography>
      <Typography variant="body1" color="text.secondary">
        View all captured leads
      </Typography>
    </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search leads..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        
        <Button
          variant="outlined"
          startIcon={<RotateCcw size={18} />}
          onClick={fetchLeads}
        >
          Refresh
        </Button>
        
        <Button
          variant="contained"
          startIcon={<Download size={18} />}
          onClick={exportToCSV}
          disabled={filteredLeads.length === 0}
        >
          Export CSV
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredLeads.length} of {leads.length} leads
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Created At</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? 'No leads found matching your search.' : 'No leads found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLeads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>{lead.name}</TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={lead.source} 
                      color={getSourceColor(lead.source)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(lead.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminLeads;